import React, { useState, useEffect } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { Modal } from './Modal';
import { Button } from './Button';
import { toast } from './Toast';

export const PushPromptModal: React.FC = () => {
  const { t } = useI18n();
  const { isSupported, isSubscribed, subscribe, loading } = usePushNotifications();
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (loading || !isSupported || isSubscribed) return;
    
    if (typeof Notification === 'undefined' || Notification.permission !== 'default') {
      return;
    }

    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
    if (!isStandalone) {
      return;
    }

    const dismissedUntil = localStorage.getItem('push_prompt_dismissed_until');
    if (dismissedUntil && Date.now() < parseInt(dismissedUntil, 10)) {
      return;
    }

    // Delay a bit so it doesn't pop up instantly over everything
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 2500);

    return () => clearTimeout(timer);
  }, [loading, isSupported, isSubscribed]);

  const handleDismiss = () => {
    // Postpone for 1 week (7 days * 24 h * 60 m * 60 s * 1000 ms)
    const oneWeek = 7 * 24 * 60 * 60 * 1000;
    localStorage.setItem('push_prompt_dismissed_until', (Date.now() + oneWeek).toString());
    setIsVisible(false);
  };

  const handleSubscribe = async () => {
    setIsLoading(true);
    const success = await subscribe();
    setIsLoading(false);
    
    if (success) {
      toast(t('push_on_toast'));
      setIsVisible(false);
    } else {
      if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
        setIsVisible(false);
      } else {
        toast(t('push_err_toast'));
      }
    }
  };

  return (
    <Modal isOpen={isVisible} onClose={handleDismiss} title={t('push_notif')} zIndex={250}>
      <div style={{ textAlign: 'center', padding: '8px 0' }}>
        <p style={{ marginBottom: '24px', fontSize: '16px', color: 'var(--cab-text)' }}>
          {t('push_prompt_msg')}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Button 
            variant="primary" 
            onClick={handleSubscribe} 
            isLoading={isLoading}
          >
            {t('turn_on')}
          </Button>
          <Button 
            variant="ghost" 
            onClick={handleDismiss}
            disabled={isLoading}
          >
            {t('push_later')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
